/* eslint-disable @backstage/no-undeclared-imports */
import { useEffect, useState } from 'react';
import {
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Chip,
  Typography,
} from '@material-ui/core';
import { Alert } from '@mui/material';
import Stack from '@mui/material/Stack';
import { makeStyles } from '@material-ui/core/styles';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  root: { marginTop: theme.spacing(2) },
  section: { marginBottom: theme.spacing(3) },
  templateChip: { margin: theme.spacing(0.5) },
  templatesContainer: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
}));

const BUILD_TYPE_TEMPLATES: Record<string, string> = {
  Helm: 'Compose.Build.Helm.ECR',
  Iac: 'Terraform',
  Docker: 'Compose.Build.Docker.ECR',
};

const EXTRA_TEMPLATES: Record<string, string> = {
  CodeArtifact: 'Compose.Build.AWSCodeartifact',
  Octopus: 'Compose.Deploy.Octopus.PushRelease',
};

const BUILD_CONFIG_NAMES = ['features', 'develop', 'main', 'releases'];

const BUILD_CONFIG_TEMPLATES: Record<string, string> = {
  features: 'Compose.Features.GH',
  develop: 'Compose.Develop.GH',
  main: 'Compose.Main.GH',
  releases: 'Compose.Releases.GH',
};

export const TeamcityBuildTypeConfiguration = ({
  onChange,
  rawErrors,
  formData,
}: any) => {
  const classes = useStyles();
  const [selectedBuildTypes, setSelectedBuildTypes] = useState<string[]>(formData?.buildTypes || []);
  const [selectedExtras, setSelectedExtras] = useState<string[]>(formData?.buildExtras || []);
  const [selectedBuildConfigNames, setSelectedBuildConfigNames] = useState<string[]>(formData?.buildConfigNames || []);

  const getSortedBuildConfigNames = () => {
    const configOrder = ['develop', 'features', 'main', 'releases'];
    return [...selectedBuildConfigNames].sort((a, b) => {
      return configOrder.indexOf(a) - configOrder.indexOf(b);
    });
  };

  const getTemplateOrder = () => {
    const sortedConfigs = getSortedBuildConfigNames();
    const order = [
      ...sortedConfigs.map(name => BUILD_CONFIG_TEMPLATES[name]),
      ...selectedBuildTypes.map(type => BUILD_TYPE_TEMPLATES[type]),
    ];

    if (selectedExtras.includes('CodeArtifact')) {
      order.push(EXTRA_TEMPLATES.CodeArtifact);
    }
    if (selectedExtras.includes('Octopus')) {
      order.push(EXTRA_TEMPLATES.Octopus);
    }

    return order;
  };

  const getVisibleTemplates = () => [
    ...selectedBuildTypes.map(type => BUILD_TYPE_TEMPLATES[type]),
    ...selectedExtras.filter(e => e === 'CodeArtifact').map(() => EXTRA_TEMPLATES.CodeArtifact),
    ...selectedExtras.filter(e => e === 'Octopus').map(() => EXTRA_TEMPLATES.Octopus),
  ];

  useEffect(() => {
    if (!selectedBuildTypes.length || !selectedBuildConfigNames.length) {
      onChange(undefined);
    } else {
      const sortedConfigs = getSortedBuildConfigNames();
      onChange({
        buildTypes: selectedBuildTypes,
        buildExtras: selectedExtras,
        buildConfigNames: sortedConfigs,
        templateOrder: getTemplateOrder(),
      });
    }
  }, [selectedBuildTypes, selectedExtras, selectedBuildConfigNames]);

  const toggleSelection = (value: string, setState: React.Dispatch<React.SetStateAction<string[]>>) => {
    setState(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const hasSelectedBuildTypes = selectedBuildTypes.length > 0;

  const renderCheckboxGroup = (
    title: string,
    caption: string,
    items: string[],
    selected: string[],
    onItemChange: (value: string) => void,
    required = false,
  ) => (
    <Box className={classes.section}>
      <FormControl component="fieldset" required={required}>
        <FormLabel component="legend">
          <Typography variant="h6">{title}</Typography>
          <Typography variant="caption">{caption}</Typography>
        </FormLabel>
        <FormGroup>
          {items.map(item => (
            <FormControlLabel
              key={item}
              control={
                <Checkbox
                  checked={selected.includes(item)}
                  onChange={() => onItemChange(item)}
                  color="primary"
                />
              }
              label={item}
            />
          ))}
        </FormGroup>
      </FormControl>
    </Box>
  );

  return (
    <Box className={classes.root}>
      {rawErrors?.length > 0 && (
        <Alert severity="error" style={{ marginBottom: '16px' }}>
          {!hasSelectedBuildTypes 
            ? 'Please select at least one build type'
            : 'Please select at least one build configuration name'}
        </Alert>
      )}
      
      {renderCheckboxGroup(
        'Build Type',
        'Select the build types for your project',
        Object.keys(BUILD_TYPE_TEMPLATES),
        selectedBuildTypes,
        (type) => toggleSelection(type, setSelectedBuildTypes),
        true,
      )}

      {renderCheckboxGroup(
        'Build Configuration Names',
        'Select which build configurations to create for the selected build types',
        BUILD_CONFIG_NAMES,
        selectedBuildConfigNames,
        (name) => toggleSelection(name, setSelectedBuildConfigNames),
        true,
      )}

      {hasSelectedBuildTypes && (
        <>
          {renderCheckboxGroup(
            'Build Extras',
            'Optional additional templates',
            Object.keys(EXTRA_TEMPLATES),
            selectedExtras,
            (extra) => toggleSelection(extra, setSelectedExtras),
          )}

          <Box className={classes.templatesContainer}>
            <Typography variant="subtitle2" gutterBottom>
              Templates that will be applied (in order):
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {getVisibleTemplates().map((template, index) => (
                <Chip
                  key={`${template}-${index}`}
                  label={`${index + 1}. ${template}`}
                  color="primary"
                  size="small"
                  className={classes.templateChip}
                />
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
};

export const TeamcityBuildTypeConfigurationExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TeamcityBuildTypeConfiguration',
    component: TeamcityBuildTypeConfiguration,
  }),
);
